import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import * as Icons from 'lucide-react';
import * as htmlToImage from 'html-to-image';

const MONTH_NAMES = ['','Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const TIER_STYLES = {
  oro: { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-400', icon: '🥇', label: 'Oro' },
  plata: { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-400', icon: '🥈', label: 'Plata' },
  bronce: { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-400', icon: '🥉', label: 'Bronce' },
};
const fmt = (v) => v ? new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(v) : '$0';

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return dateStr;
  }
};

const timeSince = (dateStr) => {
  if (!dateStr) return '—';
  try {
    const d = new Date(dateStr);
    const now = new Date();
    const diffTime = Math.abs(now - d);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 1) return 'Hoy';
    if (diffDays === 1) return 'Ayer';
    if (diffDays < 30) return `${diffDays} días`;
    const diffMonths = Math.floor(diffDays / 30);
    if (diffMonths === 1) return '1 mes';
    if (diffMonths < 12) return `${diffMonths} meses`;
    const diffYears = Math.floor(diffMonths / 12);
    return diffYears === 1 ? '1 año' : `${diffYears} años`;
  } catch { return '—'; }
};

const CardItem = ({ c, tier, isExport = false, copying = false, onCopy, onDownload }) => (
  <div className={`relative border-2 ${tier.border} rounded-2xl overflow-hidden bg-white shadow-xl ${isExport ? 'w-[500px]' : 'w-full'}`} style={{ minHeight: isExport ? 650 : 320 }}>
    {/* Header gradient */}
    <div className="bg-gradient-to-r from-purple-800 via-purple-600 to-indigo-600 px-6 py-8 text-white relative">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="bg-white p-2.5 rounded-xl shadow-lg border border-purple-100">
            <img src="/logo-tf.png" alt="Topping Frozen" className="h-14 w-auto object-contain" />
          </div>
          <div>
            <p className="text-white font-black text-2xl tracking-tight uppercase leading-none mb-1">Topping Frozen</p>
            <p className="text-purple-100 text-base font-semibold">Complementa tus ideas</p>
          </div>
        </div>
        <div className={`${tier.bg} ${tier.text} px-5 py-2 rounded-full text-base font-black shadow-md flex items-center gap-1.5`}>
          {tier.icon} {tier.label}
        </div>
      </div>
      {/* Festive stars decoration */}
      <div className="absolute top-0 right-10 opacity-20"><Icons.Star className="w-10 h-10 rotate-12" /></div>
      <div className="absolute top-4 left-1/2 opacity-10"><Icons.Star className="w-6 h-6 -rotate-12" /></div>
      <div className="absolute bottom-4 right-1/4 opacity-15"><Icons.Star className="w-8 h-8 rotate-45" /></div>
      <div className="absolute top-1/2 left-1/3 opacity-10"><Icons.Star className="w-7 h-7 -rotate-45" /></div>
      
      {/* Action buttons - hidden in export */}
      {!isExport && (
        <div className="no-export absolute top-2 right-2 flex gap-1 print:hidden">
          <button 
            onClick={onCopy}
            disabled={copying}
            className="bg-white/20 hover:bg-white/40 p-2 rounded-full backdrop-blur-md transition-all text-white"
            title="Copiar para WhatsApp"
          >
            {copying ? <Icons.Loader className="w-4 h-4 animate-spin" /> : <Icons.Copy className="w-4 h-4" />}
          </button>
          <button 
            onClick={onDownload}
            disabled={copying}
            className="bg-white/20 hover:bg-white/40 p-2 rounded-full backdrop-blur-md transition-all text-white"
            title="Descargar como imagen"
          >
            <Icons.Download className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
    
    {/* Body */}
    <div className="px-8 py-8">
      <div className="flex items-center gap-3 mb-4">
        <span className="text-3xl">🎉</span>
        <h3 className="text-3xl font-black text-gray-900 leading-tight">¡Feliz Aniversario!</h3>
      </div>
      
      <p className="text-purple-700 font-black text-3xl mb-2 uppercase tracking-tight">{c.customer_name}</p>
      
      <div className="bg-purple-50 inline-block px-4 py-2 rounded-xl mb-4">
        <p className="text-gray-700 text-lg">
          Hoy celebramos <span className="font-black text-purple-600 text-2xl">{c.anniversary_year}</span> {c.anniversary_year === 1 ? 'año' : 'años'} de éxito compartido
        </p>
      </div>

      <p className="text-gray-500 text-sm font-medium flex items-center gap-1.5 mb-6">
        <Icons.Calendar className="w-4 h-4" /> Miembro desde: {formatDate(c.first_purchase_date)}
      </p>

      {/* Gratitude Message */}
      <div className="text-gray-700 text-base italic mb-8 border-l-4 border-purple-500 pl-6 py-4 bg-purple-50/40 rounded-r-2xl whitespace-pre-line leading-relaxed font-semibold shadow-sm">
        “Emprender es creer cuando nadie más lo hace.
        Hoy celebramos tu constancia, tu disciplina y tu sueño.
        Gracias por dejarnos acompañarte en este camino. ¡Sigamos creciendo juntos!”
      </div>

      {/* Discount Card */}
      <div className="mt-4 bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-2xl p-6 text-center shadow-inner relative overflow-hidden">
        <div className="absolute -right-3 -top-3 opacity-10 rotate-12">
          <Icons.Gift className="w-20 h-20 text-green-600" />
        </div>
        <p className="text-green-800 font-black text-sm uppercase tracking-widest mb-2">🎁 Regalo Especial</p>
        <div className="flex items-center justify-center gap-4">
          <span className="text-green-700 text-6xl font-black tracking-tighter">5%</span>
          <div className="text-left">
            <p className="text-green-700 text-xl font-black leading-tight uppercase">Descuento</p>
            <p className="text-green-600 text-xs font-bold">En tu próximo pedido</p>
          </div>
        </div>
      </div>
    </div>

    {/* Footer decoration */}
    <div className="absolute bottom-0 left-0 right-0 h-3 bg-gradient-to-r from-yellow-400 via-red-500 via-purple-600 to-green-500"></div>
  </div>
);

const AnniversaryCampaignPage = () => {
  const [eligible, setEligible] = useState([]);
  const [potential, setPotential] = useState([]);
  const [summary, setSummary] = useState(null);
  const [month, setMonth] = useState(new Date().getMonth() + 2 > 12 ? 1 : new Date().getMonth() + 2);
  const [year, setYear] = useState(new Date().getMonth() + 2 > 12 ? new Date().getFullYear() + 1 : new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [copying, setCopying] = useState(null);
  const [exportCustomer, setExportCustomer] = useState(null);
  const [monthlySummary, setMonthlySummary] = useState([]);
  const [search, setSearch] = useState('');
  const [tierFilter, setTierFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('eligible');
  const [showCards, setShowCards] = useState(false);
  const [bonuses, setBonuses] = useState([]);

  const fetchEligible = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.get('/anniversary/eligible', { params: { month, year } });
      if (resp.data?.success) {
        setEligible(resp.data.data || []);
        setPotential(resp.data.potential || []);
        setSummary(resp.data.summary);
      }
    } catch (error) {
      toast.error('Error al obtener clientes elegibles');
    } finally {
      setLoading(false);
    }
  }, [month, year]);

  const fetchBonuses = useCallback(async () => {
    try {
      const resp = await api.get('/anniversary/bonuses', { params: { month, year } });
      if (resp.data?.success) setBonuses(resp.data.data || []);
    } catch { }
  }, [month, year]);

  const fetchMonthlySummary = useCallback(async () => {
    try {
      const resp = await api.get('/anniversary/monthly-summary', { params: { year } });
      if (resp.data?.success) setMonthlySummary(resp.data.data || []);
    } catch { }
  }, [year]);

  useEffect(() => { fetchEligible(); fetchBonuses(); }, [fetchEligible, fetchBonuses]);
  useEffect(() => { fetchMonthlySummary(); }, [fetchMonthlySummary]);

  const generateSingleBonus = async (c) => {
    try {
      const resp = await api.post('/anniversary/generate', { month, year, customers: [c] });
      if (resp.data?.success) {
        toast.success('Bono generado');
        fetchEligible();
      }
    } catch { toast.error('Error'); }
  };

  const handleGenerate = async () => {
    try {
      const resp = await api.post('/anniversary/generate', { month, year, customers: eligible });
      if (resp.data?.success) {
        toast.success(`${resp.data.created} bonos generados`);
        fetchEligible();
      }
    } catch { toast.error('Error'); }
  };

  const copyCard = async (customer, action = 'copy') => {
    setCopying(customer.siigo_customer_id || customer.customer_name);
    setExportCustomer(customer);
    setTimeout(async () => {
      const el = document.getElementById('hidden-export-card');
      if (!el) { setCopying(null); return; }
      try {
        const dataUrl = await htmlToImage.toPng(el, { pixelRatio: 2, backgroundColor: '#ffffff', cacheBust: true });
        if (action === 'download') {
          const link = document.createElement('a');
          link.download = `tarjeta-aniversario-${customer.customer_name}.png`;
          link.href = dataUrl;
          link.click();
          toast.success('¡Tarjeta descargada!');
          setCopying(null);
          return;
        }
        const res = await fetch(dataUrl);
        const blob = await res.blob();
        if (navigator.clipboard && window.ClipboardItem) {
          const item = new ClipboardItem({ "image/png": blob });
          await navigator.clipboard.write([item]);
          toast.success('¡Copiado!');
        }
      } finally { setCopying(null); }
    }, 150);
  };

  const currentList = activeTab === 'eligible' ? eligible : potential;
  const filtered = currentList.filter(c => {
    const matchesSearch = c.customer_name.toLowerCase().includes(search.toLowerCase());
    const matchesTier = tierFilter === 'all' || c.tier === tierFilter;
    return matchesSearch && matchesTier;
  });

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Icons.Gift className="w-7 h-7 text-purple-600" /> Campaña de Aniversario
        </h1>
        <div className="flex gap-3">
          <div className="flex bg-gray-100 p-1 rounded-lg">
            <select value={month} onChange={e => setMonth(Number(e.target.value))} className="bg-transparent text-sm py-1 px-2 font-bold outline-none">
              {MONTH_NAMES.slice(1).map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
            </select>
            <input type="number" value={year} onChange={e => setYear(Number(e.target.value))} className="bg-transparent text-sm py-1 px-2 w-20 font-bold outline-none border-l border-gray-200" />
          </div>
          <input type="text" placeholder="Buscar cliente..." value={search} onChange={e => setSearch(e.target.value)} className="form-input text-sm py-1.5 px-3 border rounded-md" />
          <button onClick={fetchEligible} className="btn btn-primary text-sm" disabled={loading}>
            <Icons.RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Eligibility Criteria Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6 flex items-start gap-3 shadow-sm">
        <Icons.Info className="w-5 h-5 text-blue-600 mt-0.5" />
        <div>
          <h4 className="text-sm font-bold text-blue-800 uppercase tracking-tight mb-1">Fórmula de Elegibilidad</h4>
          <p className="text-xs text-blue-700 leading-relaxed">
            Participan los clientes que cumplen dos condiciones: 
            <span className="font-bold ml-1">1. Antigüedad:</span> Mínimo 1 año desde su primera compra (Ciclo {year}).
            <span className="font-bold ml-3">2. Actividad:</span> Mínimo 10 pedidos facturados en los últimos 12 meses.
          </p>
        </div>
      </div>

      {/* Monthly overview */}
      {monthlySummary.length > 0 && (
        <div className="card mb-6">
          <div className="card-content p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-gray-700 text-sm flex items-center gap-2">
                📊 Clientes elegibles por mes 
                <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded text-[10px] font-black">
                  TOTAL AÑO: {monthlySummary.reduce((sum, m) => sum + m.total, 0)}
                </span>
              </h3>
              <span className="text-[10px] text-gray-400 font-medium">Ciclo {year}</span>
            </div>
            <div className="grid grid-cols-6 md:grid-cols-12 gap-2">
              {monthlySummary.map(m => (
                <button key={m.month} onClick={() => setMonth(m.month)}
                  className={`p-2 rounded-lg text-center transition-all ${month === m.month ? 'bg-purple-600 text-white ring-2 ring-purple-300' : 'bg-gray-50 hover:bg-gray-100 text-gray-700'}`}>
                  <div className="text-[10px] font-black uppercase">{MONTH_NAMES[m.month]?.slice(0, 3)}</div>
                  <div className="text-lg font-black">{m.total}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="card bg-purple-50/50 border-purple-100 p-4">
          <p className="text-[10px] uppercase font-black text-purple-600 mb-1">Elegibles {MONTH_NAMES[month]}</p>
          <p className="text-3xl font-black text-purple-900">{summary?.total || 0}</p>
        </div>
        <div className="card bg-blue-50/50 border-blue-100 cursor-pointer hover:bg-blue-100 transition-colors p-4" onClick={() => setActiveTab('potential')}>
          <p className="text-[10px] uppercase font-black text-blue-600 mb-1">Por Calificar (7-9 ped)</p>
          <p className="text-3xl font-black text-blue-900">{summary?.potential || 0}</p>
        </div>
        <div className="card bg-yellow-50/50 border-yellow-100 p-4">
          <p className="text-[10px] uppercase font-black text-yellow-600 mb-1">🥇 Oro</p>
          <p className="text-3xl font-black text-yellow-900">{summary?.oro || 0}</p>
        </div>
        <div className="card bg-gray-50/50 border-gray-100 p-4">
          <p className="text-[10px] uppercase font-black text-gray-600 mb-1">🥈 Plata</p>
          <p className="text-3xl font-black text-gray-900">{summary?.plata || 0}</p>
        </div>
      </div>

      <div className="flex gap-2 mb-4">
          <button onClick={() => setShowCards(!showCards)} className="btn btn-secondary text-sm">
            {showCards ? <><Icons.Table className="w-4 h-4 mr-2" /> Ver Tabla</> : <><Icons.CreditCard className="w-4 h-4 mr-2" /> Ver Tarjetas</>}
          </button>
          {activeTab === 'eligible' && (
            <button onClick={handleGenerate} className="btn btn-primary text-sm bg-green-600 border-green-600 hover:bg-green-700">
              <Icons.Zap className="w-4 h-4 mr-2" /> Generar Todos ({eligible.length})
            </button>
          )}
      </div>

      {showCards ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((c, i) => (
            <CardItem key={i} c={c} tier={TIER_STYLES[c.tier] || TIER_STYLES.bronce} copying={copying === (c.siigo_customer_id || c.customer_name)} onCopy={() => copyCard(c, 'copy')} onDownload={() => copyCard(c, 'download')} />
          ))}
        </div>
      ) : (
        <div className="card">
          <div className="card-content p-0 overflow-hidden">
            <div className="flex border-b border-gray-100 bg-gray-50/30">
              <button onClick={() => setActiveTab('eligible')} className={`px-6 py-4 text-sm font-bold transition-all border-b-2 flex items-center gap-2 ${activeTab === 'eligible' ? 'border-purple-600 text-purple-700 bg-white' : 'border-transparent text-gray-500'}`}>
                <Icons.Award className="w-4 h-4" /> Clientes Elegibles ({eligible.length})
              </button>
              <button onClick={() => setActiveTab('potential')} className={`px-6 py-4 text-sm font-bold transition-all border-b-2 flex items-center gap-2 ${activeTab === 'potential' ? 'border-blue-600 text-blue-700 bg-white' : 'border-transparent text-gray-500'}`}>
                <Icons.Target className="w-4 h-4" /> Próximos a Calificar ({potential.length})
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50/50 text-gray-500 uppercase tracking-wider border-b border-gray-100">
                  <tr>
                    <th className="py-2.5 px-3 text-left font-semibold text-xs">#</th>
                    <th className="text-left py-2.5 px-3 font-semibold text-xs">Cliente</th>
                    <th className="text-center py-2.5 px-2 font-semibold text-xs">Tier</th>
                    <th className="text-center py-2.5 px-2 font-semibold text-xs">Años</th>
                    <th className="text-center py-2.5 px-2 font-semibold text-xs">Primera Compra</th>
                    <th className="text-center py-2.5 px-2 font-semibold text-xs">Última Compra</th>
                    <th className="text-right py-2.5 px-2 font-semibold text-xs">Pedidos 12M</th>
                    <th className="text-right py-2.5 px-2 font-semibold text-xs">Revenue 12M</th>
                    <th className="text-left py-2.5 px-2 font-semibold text-xs">Estado</th>
                    <th className="text-center py-2.5 px-2 font-semibold text-xs">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((c, i) => {
                    const tier = TIER_STYLES[c.tier] || TIER_STYLES.bronce;
                    const bonus = c.existing_bonus;
                    const isPotential = activeTab === 'potential';
                    return (
                      <tr key={i} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                        <td className="py-2.5 px-3 text-gray-400 text-xs font-medium">{i + 1}</td>
                        <td className="py-2.5 px-3 font-bold text-gray-900">{c.customer_name}</td>
                        <td className="py-2.5 px-2 text-center">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase ${tier.bg} ${tier.text}`}>
                            {tier.icon} {tier.label}
                          </span>
                        </td>
                        <td className="py-2.5 px-2 text-center font-black text-purple-700 text-base">{c.anniversary_year}</td>
                        <td className="py-2.5 px-2 text-center text-xs text-gray-500 whitespace-nowrap">
                          {formatDate(c.first_purchase_date)}
                        </td>
                        <td className="py-2.5 px-2 text-center whitespace-nowrap">
                          <div className="flex flex-col">
                            <span className="text-xs text-gray-500">{formatDate(c.last_purchase)}</span>
                            <span className="text-[10px] font-bold text-purple-600 uppercase">Hace {timeSince(c.last_purchase)}</span>
                          </div>
                        </td>
                        <td className="py-2.5 px-2 text-right">
                          <div className="flex flex-col items-end">
                            <span className={`font-black ${isPotential ? 'text-blue-600' : 'text-gray-900'}`}>{c.orders_12m} / 10</span>
                            {isPotential && <span className="text-[10px] text-blue-400 font-bold">Faltan {10 - c.orders_12m}</span>}
                          </div>
                        </td>
                        <td className="py-2.5 px-2 text-right font-bold text-green-700">{fmt(c.revenue_12m)}</td>
                        <td className="py-2.5 px-2">
                          {isPotential ? (
                            <span className="flex items-center gap-1.5 text-blue-600 font-bold text-xs uppercase tracking-tighter">
                              <Icons.Zap className="w-3 h-3 animate-pulse" /> Potencial
                            </span>
                          ) : (
                            bonus ? (
                              <span className="flex items-center gap-1.5 text-green-600 font-bold text-xs uppercase tracking-tighter">
                                <Icons.CheckCircle className="w-3.5 h-3.5" /> {bonus.bonus_status === 'applied' ? 'Canjeado' : 'Generado'}
                              </span>
                            ) : (
                              <span className="text-gray-400 italic text-xs font-medium">Sin generar</span>
                            )
                          )}
                        </td>
                        <td className="py-2.5 px-2 text-center">
                          {!isPotential && !bonus && (
                            <button onClick={() => generateSingleBonus(c)} className="text-purple-600 hover:text-purple-800 p-1.5 rounded-lg hover:bg-purple-50 transition-all">
                              <Icons.PlusSquare className="w-5 h-5" />
                            </button>
                          )}
                          {isPotential && (
                            <button onClick={() => toast(`Llamar a ${c.customer_name}: ${c.orders_12m} pedidos`, { icon: '📞' })} className="text-blue-600 hover:text-blue-800 p-1.5 rounded-lg hover:bg-blue-50 transition-all">
                              <Icons.Phone className="w-5 h-5" />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {filtered.length === 0 && (
                    <tr><td colSpan={10} className="py-12 text-center text-gray-400 font-medium">
                      {loading ? 'Cargando datos...' : 'No se encontraron clientes'}
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Hidden Card for Export */}
      <div style={{ position: 'fixed', left: '-9999px', top: '0', zIndex: -100 }}>
        {exportCustomer && (
          <div id="hidden-export-card">
            <CardItem c={exportCustomer} tier={TIER_STYLES[exportCustomer.tier] || TIER_STYLES.bronce} isExport={true} />
          </div>
        )}
      </div>

      <style>{`
        @media print {
          nav, header, .print\\:hidden, button, select, input, .no-export { display: none !important; }
          body { background: white !important; }
          .card { border: none !important; box-shadow: none !important; }
        }
      `}</style>
    </div>
  );
};

export default AnniversaryCampaignPage;
